import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Layout from './components/layout/Layout';
import RequireBetaAccess from './beta/RequireBetaAccess';
import { BetaMixDraftsProvider } from './beta/mixDrafts';
import RequireSecureAccess from './security/RequireSecureAccess';

// Page imports (to be created by agents)
import HomePage from './components/pages/HomePage';
import AboutPage from './components/pages/AboutPage';
import PastryMixesPage from './components/pages/PastryMixesPage';
import BakeryMixesPage from './components/pages/BakeryMixesPage';
import RecipesPage from './components/pages/RecipesPage';
import RecipeDetailPage from './components/pages/RecipeDetailPage';
import { recipes } from './data/recipes';
import ManufacturingPage from './components/pages/ManufacturingPage';
import QAPage from './components/pages/QAPage';
import ContactPage from './components/pages/ContactPage';
import MirageDifferencePage from './components/pages/MirageDifferencePage';
import SuperiorProductsPage from './components/pages/SuperiorProductsPage';
import CredentialsPage from './components/pages/CredentialsPage';
import NotFoundPage from './components/pages/NotFoundPage';

const AccessPortalPage = lazy(() => import('./components/pages/AccessPortalPage'));
const SamplesPage = lazy(() => import('./components/pages/SamplesPage'));
const OperationsFlowPage = lazy(() => import('./components/pages/considerations/OperationsFlowPage'));
const KpiConsiderationsPage = lazy(() => import('./components/pages/considerations/KpiConsiderationsPage'));
const CompetitorAnalysisPage = lazy(() => import('./components/pages/considerations/CompetitorAnalysisPage'));
const BetaFeaturesHubPage = lazy(() => import('./components/pages/BetaFeaturesHubPage'));
const EmailAutomationPage = lazy(() => import('./components/pages/EmailAutomationPage'));
const DocumentFlowPage = lazy(() => import('./components/pages/DocumentFlowPage'));
const SeoStrategyPage = lazy(() => import('./components/pages/SeoStrategyPage'));
const BidorStyleCatalogPage = lazy(() => import('./components/pages/BidorStyleCatalogPage'));
const EasyAddAdminPage = lazy(() => import('./components/pages/EasyAddAdminPage'));
const ProductionCalendarPage = lazy(() => import('./components/pages/ProductionCalendarPage'));
const ProductionEstimatorPage = lazy(() => import('./components/pages/ProductionEstimatorPage'));

const siteRoutes = [
  { path: '/', element: <HomePage /> },
  { path: '/about-us', element: <AboutPage /> },
  { path: '/pastry-mixes', element: <PastryMixesPage /> },
  { path: '/bakery-mixes', element: <BakeryMixesPage /> },
  { path: '/mirage-recipe', element: <RecipesPage /> },
  { path: '/excellence-in-manufacturing', element: <ManufacturingPage /> },
  { path: '/q-a', element: <QAPage /> },
  { path: '/contact-us', element: <ContactPage /> },
  { path: '/the-mirage-difference', element: <MirageDifferencePage /> },
  { path: '/superior-products', element: <SuperiorProductsPage /> },
  { path: '/our-credentials', element: <CredentialsPage /> }
];

const buildRoutePath = (prefix, path) => {
  if (!prefix) {
    return path;
  }

  if (path === '/') {
    return prefix;
  }

  return `${prefix}${path}`;
};

const withAccessGate = (element, isProtected) => {
  if (!isProtected) {
    return element;
  }

  return <RequireBetaAccess>{element}</RequireBetaAccess>;
};

function App() {
  const withSuspense = (element) => (
    <Suspense
      fallback={
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto text-center text-gray-500">Loading...</div>
        </section>
      }
    >
      {element}
    </Suspense>
  );

  const renderSiteRoutes = (prefix = '', isProtected = false) => (
    <>
      {siteRoutes.map((route) => (
        <Route
          key={`${prefix || 'live'}${route.path}`}
          path={buildRoutePath(prefix, route.path)}
          element={withAccessGate(route.element, isProtected)}
        />
      ))}

      {recipes.map((recipe) => (
        <Route
          key={`${prefix || 'live'}${recipe.id}`}
          path={buildRoutePath(prefix, recipe.path)}
          element={withAccessGate(<RecipeDetailPage recipe={recipe} />, isProtected)}
        />
      ))}
    </>
  );

  return (
    <BetaMixDraftsProvider>
      <Router>
        <Layout>
          <Routes>
          <Route path="/access" element={withSuspense(<AccessPortalPage />)} />
          <Route path="/acess" element={<Navigate to="/access" replace />} />
          <Route path="/beta" element={<Navigate to="/access" replace />} />
          <Route
            path="/considerations/operations-flow"
            element={
              <RequireSecureAccess>
                {withSuspense(<OperationsFlowPage />)}
              </RequireSecureAccess>
            }
          />
          <Route
            path="/considerations/kpis"
            element={
              <RequireSecureAccess>
                {withSuspense(<KpiConsiderationsPage />)}
              </RequireSecureAccess>
            }
          />
          <Route
            path="/considerations/competitor-analysis"
            element={
              <RequireSecureAccess>
                {withSuspense(<CompetitorAnalysisPage />)}
              </RequireSecureAccess>
            }
          />
          <Route
            path="/considerations"
            element={<Navigate to="/considerations/operations-flow" replace />}
          />
          <Route
            path="/operations-flow"
            element={<Navigate to="/considerations/operations-flow" replace />}
          />
          <Route
            path="/kpis"
            element={<Navigate to="/considerations/kpis" replace />}
          />
          <Route
            path="/competitor-analysis"
            element={<Navigate to="/considerations/competitor-analysis" replace />}
          />
          <Route
            path="/beta/:program/samples"
            element={
              <RequireBetaAccess>
                {withSuspense(<SamplesPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/features"
            element={
              <RequireBetaAccess>
                {withSuspense(<BetaFeaturesHubPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/email-automation"
            element={
              <RequireBetaAccess>
                {withSuspense(<EmailAutomationPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/email-generator"
            element={
              <RequireBetaAccess>
                {withSuspense(<EmailAutomationPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/document-flow"
            element={
              <RequireBetaAccess>
                {withSuspense(<DocumentFlowPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/seo-lab"
            element={
              <RequireBetaAccess>
                {withSuspense(<SeoStrategyPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/mixes-catalog"
            element={
              <RequireBetaAccess>
                {withSuspense(<BidorStyleCatalogPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/admin"
            element={
              <RequireBetaAccess>
                {withSuspense(<EasyAddAdminPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/production-calendar"
            element={
              <RequireBetaAccess>
                {withSuspense(<ProductionCalendarPage />)}
              </RequireBetaAccess>
            }
          />
          <Route
            path="/beta/:program/production-estimator"
            element={
              <RequireBetaAccess>
                {withSuspense(<ProductionEstimatorPage />)}
              </RequireBetaAccess>
            }
          />
          {renderSiteRoutes('/beta/:program', true)}
          <Route
            path="/beta/:program/*"
            element={
              <RequireBetaAccess>
                <NotFoundPage />
              </RequireBetaAccess>
            }
          />

          {renderSiteRoutes()}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </Router>
    </BetaMixDraftsProvider>
  );
}

export default App;
